import { z } from 'zod'
import {
  CommunicationKindSchema,
  CommunicationStatusSchema,
  EmailTransportSchema,
  MemberStatusSchema,
  RecipientStatusSchema,
} from '../enums'
import { IsoDateTimeSchema, MemberIdSchema, UuidSchema } from '../primitives'
import { PaginationQuerySchema } from './common'
import { DeliveryCountsSchema } from './elections'

/**
 * Email (PLAN v3 §3 + §6 M3/M4/S6/S9, cuts): Resend SMTP through nodemailer, sent by the `email` job queue
 * (separate from the chain queue), ≤ 2 messages / s, per-recipient status, plain-text body in a fixed HTML template
 * (escaped) with the organization name, a CTA button (invitations), and a footer "you receive this because you are
 * a member of X" + one-click opt-out link (List-Unsubscribe + List-Unsubscribe-Post: One-Click).
 * - Organizer routes: `/api/org/organizations/:orgId/communications[...]`.
 * - Test organizations never email real members (M4): recipients are rewritten to TEST_MAIL_SINK
 *   (`delivered@resend.dev`), except "send a test to me". SMTP not configured ⇒ capture transport (log / DB only).
 * - Opted-out members (and removed / non-active ones) are never emailed.
 */

/* ================================================================== transport status */

export const EmailStatusSchema = z.object({
  /** SMTP configured (else every message is captured: logged, never delivered). */
  configured: z.boolean(),
  transport: EmailTransportSchema,
  /** `DAVINCI Elections <elections@vocdoni.org>` */
  from: z.string(),
  /** Test organizations: every recipient is rewritten to this address (M4). */
  testSink: z.string(),
})
export type EmailStatus = z.infer<typeof EmailStatusSchema>

/* ================================================================== audience */

/**
 * Who receives a message: every ACTIVE member, or the active members of some districts (cuts: no status filter).
 * Members who opted out are always excluded.
 */
export const CommunicationAudienceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }),
  z.object({ kind: z.literal('districts'), districts: z.array(z.string().trim().min(1).max(200)).min(1).max(1000) }),
])
export type CommunicationAudience = z.infer<typeof CommunicationAudienceSchema>

export const COMMUNICATION_SUBJECT_MAX = 200
export const COMMUNICATION_BODY_MAX = 20_000

/** Plain text (cuts): rendered escaped in the fixed template, paragraphs on blank lines, URLs not linkified. */
export const CommunicationContentSchema = z.object({
  subject: z.string().trim().min(1, 'Subject is required').max(COMMUNICATION_SUBJECT_MAX),
  body: z.string().trim().min(1, 'Message is required').max(COMMUNICATION_BODY_MAX),
})
export type CommunicationContent = z.infer<typeof CommunicationContentSchema>

/* GET …/communications/audience?kind=all | ?kind=districts&districts=A,B → AudiencePreviewResponse (compose UI) */
export const AudiencePreviewQuerySchema = z.object({
  kind: z.enum(['all', 'districts']).default('all'),
  /** Comma-separated district names (kind = districts). */
  districts: z
    .string()
    .optional()
    .transform((v) =>
      v
        ? v
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean)
        : []
    ),
})
export type AudiencePreviewQuery = z.input<typeof AudiencePreviewQuerySchema>
export const AudiencePreviewResponseSchema = z.object({
  /** Active members with an email who will receive it. */
  recipients: z.number().int().min(0),
  /** Active members of the audience excluded because they opted out. */
  optedOut: z.number().int().min(0),
  /** The organization is in test mode: the messages will go to `email.testSink`, not to the members (M4). */
  redirectedToTestSink: z.boolean(),
  email: EmailStatusSchema,
})
export type AudiencePreviewResponse = z.infer<typeof AudiencePreviewResponseSchema>

/* ================================================================== communication */

export const CommunicationSchema = z.object({
  id: UuidSchema,
  organizationId: UuidSchema,
  kind: CommunicationKindSchema,
  /** Invitations: the voting they announce. */
  electionId: UuidSchema.nullable(),
  subject: z.string(),
  body: z.string(),
  audience: CommunicationAudienceSchema,
  status: CommunicationStatusSchema,
  counts: DeliveryCountsSchema,
  /** Rewritten to the test sink (test organization, M4). */
  redirectedToTestSink: z.boolean(),
  /** Organizer who sent it (null for automatic invitations). */
  createdBy: z.object({ id: UuidSchema, name: z.string() }).nullable(),
  lastError: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  /** When every recipient row became final. */
  completedAt: IsoDateTimeSchema.nullable(),
})
export type Communication = z.infer<typeof CommunicationSchema>

export const CommunicationResponseSchema = z.object({ communication: CommunicationSchema })
export type CommunicationResponse = z.infer<typeof CommunicationResponseSchema>

/*
 * POST …/communications → 201 CommunicationResponse (status queued). Recipients are pre-inserted (M3) and the
 * `send_communication` job is enqueued on the email queue. 422 no_recipients when the audience is empty.
 */
export const CommunicationCreateRequestSchema = CommunicationContentSchema.extend({
  audience: CommunicationAudienceSchema,
})
export type CommunicationCreateRequest = z.input<typeof CommunicationCreateRequestSchema>

/* GET …/communications?kind=&page=&pageSize= → CommunicationListResponse (history, newest first, with counts) */
export const CommunicationListQuerySchema = PaginationQuerySchema.extend({ kind: CommunicationKindSchema.optional() })
export type CommunicationListQuery = z.input<typeof CommunicationListQuerySchema>
export const CommunicationListResponseSchema = z.object({
  items: z.array(CommunicationSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  email: EmailStatusSchema,
})
export type CommunicationListResponse = z.infer<typeof CommunicationListResponseSchema>

/*
 * POST …/communications/test → TestSendResponse. Sends the message synchronously (not queued) to the ORGANIZER's own
 * email only (never rewritten to the sink). 503 email_unavailable when the transport fails.
 */
export const TestSendRequestSchema = CommunicationContentSchema
export type TestSendRequest = z.infer<typeof TestSendRequestSchema>
export const TestSendResponseSchema = z.object({
  to: z.string(),
  transport: EmailTransportSchema,
  sentAt: IsoDateTimeSchema,
})
export type TestSendResponse = z.infer<typeof TestSendResponseSchema>

/* GET …/communications/:communicationId/recipients?status=&page=&pageSize= → RecipientListResponse */
export const RecipientListQuerySchema = PaginationQuerySchema.extend({ status: RecipientStatusSchema.optional() })
export type RecipientListQuery = z.input<typeof RecipientListQuerySchema>
export const RecipientSchema = z.object({
  memberId: MemberIdSchema,
  fullName: z.string(),
  /** Member address (the delivery address may be the test sink, see the communication). */
  email: z.string().nullable(),
  memberStatus: MemberStatusSchema,
  status: RecipientStatusSchema,
  error: z.string().nullable(),
  /**
   * Delivery note. `sink-suppressed`: test organization over the per-communication cap of real deliveries to the
   * test inbox (TEST_SINK_MAX_PER_COMMUNICATION): marked sent, not delivered.
   */
  note: z.string().nullable(),
  attempts: z.number().int().min(0),
  sentAt: IsoDateTimeSchema.nullable(),
})
export type Recipient = z.infer<typeof RecipientSchema>
export const RecipientListResponseSchema = z.object({
  items: z.array(RecipientSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
})
export type RecipientListResponse = z.infer<typeof RecipientListResponseSchema>

/* ================================================================== opt-out (public, signed token) */

/**
 * The opt-out token (in every email footer and the List-Unsubscribe header) is signed by the server and identifies
 * one member of one organization. The web page `/unsubscribe#token=…` shows the info and confirms; mail clients
 * POST the same URL with `List-Unsubscribe=One-Click` (form body, ignored).
 *
 * GET  /api/public/unsubscribe/:token → UnsubscribeInfoResponse (404 invalid_unsubscribe_token)
 * POST /api/public/unsubscribe/:token → UnsubscribeInfoResponse (optedOut true; idempotent)
 */
export const UnsubscribeTokenSchema = z.string().min(16).max(512)
export const UnsubscribeInfoResponseSchema = z.object({
  organizationName: z.string(),
  /** e.g. `c*****@example.com` */
  emailMasked: z.string(),
  optedOut: z.boolean(),
})
export type UnsubscribeInfoResponse = z.infer<typeof UnsubscribeInfoResponseSchema>

/* PATCH /api/o/:orgSlug/me/email (member cookie) → MemberMeResponse: the member re-subscribes / opts out. */
export const UpdateEmailPreferencesRequestSchema = z.object({ optOut: z.boolean() })
export type UpdateEmailPreferencesRequest = z.infer<typeof UpdateEmailPreferencesRequestSchema>
